import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../../styles/theme';
import { Button } from '../common/Button';

interface BlankDefinition {
  id: string;
  type: string;
  label: string;
  placeholder: string;
  suggestions: string[];
  required: boolean;
  help_text: string;
  position: number;
  original_text?: string;
}

interface FossilTemplate {
  template: string;
  blanks: BlankDefinition[];
  domain: string;
  educational_notes: string[];
  original_prompt: string;
  basic_improvement: string;
}

interface Props {
  template: FossilTemplate;
  filledBlanks: Record<string, string>;
  originalPrompt: string;
  onEdit: () => void;
  onReset: () => void;
}

const Container = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing['2xl']};
  box-shadow: ${theme.shadows.sm};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.lg};
  }
`;

const Title = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PromptCard = styled.div<{ highlight?: boolean }>`
  background: ${props => 
    props.highlight ? 
    `linear-gradient(135deg, ${theme.colors.primaryLight}20, ${theme.colors.primary}10)` : 
    theme.colors.background};
  border: 2px solid ${props => 
    props.highlight ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.xl};
  position: relative;
`;

const CardLabel = styled.div`
  position: absolute;
  top: -12px;
  left: ${theme.spacing.lg};
  background: ${theme.colors.surface};
  padding: 0 ${theme.spacing.sm};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const PromptText = styled.div`
  font-size: ${theme.fontSizes.base};
  line-height: 1.8;
  color: ${theme.colors.text.primary};
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const FilledBlank = styled.span`
  background: ${theme.colors.success}20;
  color: ${theme.colors.success};
  padding: 2px 6px;
  border-radius: ${theme.radii.sm};
  font-weight: 600;
  border-bottom: 2px solid ${theme.colors.success};
`;

const ImprovementMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing.md};
  margin: ${theme.spacing.xl} 0;
  padding: ${theme.spacing.lg};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.md};
`;

const Metric = styled.div`
  text-align: center;
`;

const MetricValue = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.xs};
`;

const MetricLabel = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const CopyButton = styled(Button)`
  flex: 1;
  white-space: nowrap;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    padding: ${theme.spacing.md} ${theme.spacing.lg};
  }
`;

const EditButton = styled(Button)`
  flex: 1;
  background: transparent;
  color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.primary};
  white-space: nowrap;
  
  &:hover {
    background: ${theme.colors.primary};
    color: white;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    padding: ${theme.spacing.md} ${theme.spacing.lg};
  }
`;

const CopyFeedback = styled(motion.div)`
  position: fixed;
  top: 20px;
  right: 20px;
  background: ${theme.colors.success};
  color: white;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.lg};
  font-weight: 600;
  z-index: 1000;
`;

export const PreviewSection: React.FC<Props> = ({
  template,
  filledBlanks,
  originalPrompt,
  onEdit,
  onReset
}) => {
  const [copied, setCopied] = useState(false);
  
  // Generate the final prompt with filled blanks
  const finalPrompt = useMemo(() => {
    let result = template.template;
    
    // Replace each blank placeholder with the filled value
    Object.entries(filledBlanks).forEach(([blankId, value]) => {
      if (value && value.trim()) {
        // Match {{blankId:anything}} - use [^}]+ to match any characters except closing brace
        const regex = new RegExp(`\\{\\{${blankId}:[^}]+\\}\\}`, 'g');
        result = result.replace(regex, value);
      }
    });
    
    return result;
  }, [template.template, filledBlanks]);
  
  // Generate highlighted version for display
  const highlightedPrompt = useMemo(() => {
    let result = template.template;
    const parts: any[] = [];
    let lastIndex = 0;
    
    const blankRegex = /\{\{(\w+):([^}]+)\}\}/g;
    let match;
    
    while ((match = blankRegex.exec(template.template)) !== null) {
      // Add text before blank
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: template.template.slice(lastIndex, match.index)
        });
      }
      
      // Add filled blank
      const blankId = match[1];
      const value = filledBlanks[blankId];
      if (value && value.trim()) {
        parts.push({
          type: 'filled',
          content: value
        });
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < template.template.length) {
      parts.push({
        type: 'text',
        content: template.template.slice(lastIndex)
      });
    }
    
    return parts;
  }, [template.template, filledBlanks]);
  
  // Calculate improvement metrics
  const metrics = {
    specificity: Math.round((finalPrompt.length / originalPrompt.length) * 100),
    structure: template.blanks.length,
    clarity: Math.round((1 - (originalPrompt.split(' ').length / finalPrompt.split(' ').length)) * 100 + 100)
  };
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(finalPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <Container>
      <Title>
        <span>🎉</span>
        Your Fossil-Filled Prompt is Ready!
      </Title>
      
      <ComparisonGrid>
        <PromptCard>
          <CardLabel>Original Prompt</CardLabel>
          <PromptText>{originalPrompt}</PromptText>
        </PromptCard>
        
        <PromptCard highlight>
          <CardLabel>Fossil-Filled Prompt</CardLabel>
          <PromptText>
            {highlightedPrompt.map((part, index) => {
              if (part.type === 'filled') {
                return <FilledBlank key={index}>{part.content}</FilledBlank>;
              }
              return <span key={index}>{part.content}</span>;
            })}
          </PromptText>
        </PromptCard>
      </ComparisonGrid>
      
      <ImprovementMetrics>
        <Metric>
          <MetricValue>{metrics.specificity}%</MetricValue>
          <MetricLabel>More Specific</MetricLabel>
        </Metric>
        <Metric>
          <MetricValue>{metrics.structure}</MetricValue>
          <MetricLabel>Key Elements</MetricLabel>
        </Metric>
        <Metric>
          <MetricValue>{metrics.clarity}%</MetricValue>
          <MetricLabel>Clarity Score</MetricLabel>
        </Metric>
      </ImprovementMetrics>
      
      <ActionButtons>
        <EditButton onClick={onEdit}>
          ← Back to Edit
        </EditButton>
        <CopyButton onClick={handleCopy}>
          📋 Copy Final Prompt
        </CopyButton>
        <Button onClick={onReset} variant="secondary">
          🔄 Start Over
        </Button>
      </ActionButtons>
      
      {copied && (
        <CopyFeedback
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          ✓ Copied to clipboard!
        </CopyFeedback>
      )}
    </Container>
  );
};